/**
 * EventDataManager.js
 * ----------------------------------------------------------------------------
 * Extracts, validates, transforms, and organizes event data into a stable
 * FullCalendar-compatible shape.
 *
 * MIGRATION CONTRACT (IMPORTANT)
 * ----------------------------------------------------------------------------
 * Canonical structured metadata should live on the RAW event as:
 *   rawEvent.locationData
 *
 * During migration we also support:
 *   rawEvent.locData (legacy)
 * and a temporary fallback where some producers encode JSON into a location string.
 *
 * This manager normalizes output so renderers should rely on:
 *   event.extendedProps.types        -> string[]
 *   event.extendedProps.locationData -> object|null
 *
 * and treat:
 *   event.extendedProps.location     -> display text only (string)
 *
 * Once all producers reliably supply rawEvent.locationData, you can remove
 * the JSON parsing fallback in _extractLocationData().
 */

class EventDataManager {
  /**
   * @param {Element} calendarElement DOM element that contains `data-events`
   * @param {Object} options
   * @param {boolean} options.debug Enable diagnostic logging
   * @param {function} options.logger Optional logger (defaults to console when debug=true)
   */
  constructor(calendarElement, options = {}) {
    this.calendarElement = calendarElement;

    this.rawEvents = [];
    this.transformedEvents = [];
    this.errors = [];

    this.debug = !!options.debug;
    this.logger = options.logger || console;
  }

  /* ------------------------------------------------------------------
   * Parsing
   * ------------------------------------------------------------------ */

  /**
   * Parse events from DOM data attribute (`data-events`)
   * @returns {boolean} Success status
   */
  parseEventsFromDOM() {
    this.errors.length = 0;

    if (!this.calendarElement || typeof this.calendarElement.getAttribute !== 'function') {
      this.errors.push('Calendar element not provided or invalid');
      return false;
    }

    try {
      const eventsDataAttribute = this.calendarElement.getAttribute('data-events');

      if (!eventsDataAttribute) {
        this.errors.push('No data-events attribute found');
        return false;
      }

      const parsedEvents = JSON.parse(eventsDataAttribute);

      if (!Array.isArray(parsedEvents)) {
        this.errors.push('Events data is not an array');
        return false;
      }

      this.rawEvents = parsedEvents;
      this._debug(`Parsed ${this.rawEvents.length} raw events from DOM`);
      return true;
    } catch (error) {
      this.errors.push(`Parse error: ${error.message}`);
      return false;
    }
  }

  /* ------------------------------------------------------------------
   * Transformation
   * ------------------------------------------------------------------ */

  /**
   * Transform events into FullCalendar format (deterministic schema).
   * @returns {Array} FullCalendar-compatible events
   */
  transformForFullCalendar() {
    this.transformedEvents = [];

    if (!Array.isArray(this.rawEvents) || this.rawEvents.length === 0) {
      return [];
    }

    for (let i = 0; i < this.rawEvents.length; i++) {
      const rawEvent = this.rawEvents[i];

      try {
        const transformed = this._transformSingleEvent(rawEvent, i);
        if (transformed) this.transformedEvents.push(transformed);
      } catch (error) {
        this.errors.push(`Event ${i} transform error: ${error.message}`);
      }
    }

    this._debug(`Transformed ${this.transformedEvents.length} events`);
    return this.transformedEvents;
  }

  /**
   * Transform a single event from Google Calendar-like format to FullCalendar format
   * @private
   */
  _transformSingleEvent(rawEvent, index) {
    if (!rawEvent || typeof rawEvent !== 'object') {
      this.errors.push(`Event ${index}: invalid event object`);
      return null;
    }

    const title = rawEvent.summary || rawEvent.title;
    if (!title) {
      this.errors.push(`Event ${index}: missing title/summary`);
      return null;
    }

    const startTime = rawEvent.start?.dateTime || rawEvent.start?.date;
    if (!startTime) {
      this.errors.push(`Event ${index}: missing start time`);
      return null;
    }

    const endTime = rawEvent.end?.dateTime || rawEvent.end?.date || undefined;
    const isAllDay = !rawEvent.start?.dateTime && !!rawEvent.start?.date;

    const locationText = typeof rawEvent.location === 'string' ? rawEvent.location : '';

    // Canonical structured metadata
    const locationData = this._extractLocationData(rawEvent, locationText);

    // Normalized types (string[])
    const types = this._normalizeTypes(locationData?.type);

    const fullCalendarEvent = {
      id: rawEvent.id || `event-${index}`,
      title: title || 'Untitled Event',
      start: startTime,
      allDay: isAllDay,
      extendedProps: {
        description: rawEvent.description || '',
        // Display text only
        location: locationText,
        // Normalized fields for downstream consumers
        types,
        locationData: locationData || null,
        // Keep raw event for deep fallback/debugging
        originalEvent: rawEvent,
      },
    };

    if (endTime) fullCalendarEvent.end = endTime;

    return fullCalendarEvent;
  }

  /* ------------------------------------------------------------------
   * LocationData + Type normalization
   * ------------------------------------------------------------------ */

  /**
   * Extract structured location metadata.
   *
   * Preferred:
   * - rawEvent.locationData (canonical)
   * - rawEvent.locData (legacy)
   *
   * Temporary fallback:
   * - JSON.parse(rawEvent.location) if it looks like JSON
   *
   * @private
   */
  _extractLocationData(rawEvent, locationText) {
    const structured = rawEvent.locationData || rawEvent.locData;
    if (structured && typeof structured === 'object') return structured;

    // Temporary legacy fallback (remove when migration is complete)
    if (typeof locationText === 'string') {
      const trimmed = locationText.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && typeof parsed === 'object') return parsed;
        } catch {
          // ignore malformed JSON
        }
      }
    }

    return null;
  }

  /**
   * Normalize types into a clean string array.
   * @private
   */
  _normalizeTypes(typeValue) {
    if (!typeValue) return [];

    if (Array.isArray(typeValue)) {
      return typeValue
        .map((t) => (t == null ? '' : String(t).trim()))
        .filter(Boolean);
    }

    return [String(typeValue).trim()].filter(Boolean);
  }

  _debug(...args) {
    if (!this.debug) return;
    if (this.logger && typeof this.logger.debug === 'function') {
      this.logger.debug(...args);
      return;
    }
    // fallback
    this.logger?.log?.(...args);
  }

  /* ------------------------------------------------------------------
   * Stats + grouping
   * ------------------------------------------------------------------ */

  /**
   * Get event statistics
   */
  getEventStats() {
    return {
      totalRawEvents: this.rawEvents.length,
      totalTransformedEvents: this.transformedEvents.length,
      totalErrors: this.errors.length,
      hasValidationErrors: this.errors.length > 0,
      eventsByType: this.getEventsByType(),
    };
  }

  /**
   * Group transformed events by normalized type(s).
   * - events with no types are counted under "uncategorized"
   * - if an event has multiple types, each type is incremented
   */
  getEventsByType() {
    const typeGroups = {};

    for (const event of this.transformedEvents) {
      const types = Array.isArray(event.extendedProps?.types) ? event.extendedProps.types : [];

      if (!types.length) {
        typeGroups.uncategorized = (typeGroups.uncategorized || 0) + 1;
        continue;
      }

      for (const t of types) {
        const key = String(t);
        typeGroups[key] = (typeGroups[key] || 0) + 1;
      }
    }

    return typeGroups;
  }

  /* ------------------------------------------------------------------
   * Accessors (return copies to prevent accidental mutation)
   * ------------------------------------------------------------------ */

  getErrors() {
    return [...this.errors];
  }

  getRawEvents() {
    return [...this.rawEvents];
  }

  getTransformedEvents() {
    return [...this.transformedEvents];
  }

  /**
   * Get all events (alias for compatibility)
   */
  getAllEvents() {
    return this.getTransformedEvents();
  }

  /* ------------------------------------------------------------------
   * Searching
   * ------------------------------------------------------------------ */

  /**
   * Find events by criteria
   * @param {string|function|object} criteria - Search criteria
   * @returns {Array} Array of matching events
   */
  findEvents(criteria) {
    const events = this.transformedEvents;

    if (typeof criteria === 'string') {
      const q = criteria.toLowerCase();
      return events.filter((event) => event.title && event.title.toLowerCase().includes(q));
    }

    if (typeof criteria === 'function') {
      return events.filter(criteria);
    }

    if (criteria && typeof criteria === 'object') {
      return events.filter((event) => {
        return Object.keys(criteria).every((key) => {
          if (key === 'date' && event.start) {
            const eventDate = new Date(event.start);
            const searchDate = new Date(criteria[key]);
            return eventDate.toDateString() === searchDate.toDateString();
          }
          return event[key] === criteria[key];
        });
      });
    }

    return [];
  }
}

export default EventDataManager;
